import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Stack, TextField, PrimaryButton, MessageBar, MessageBarType, Spinner, SpinnerSize, Label } from "@fluentui/react";
import { getResetPasswordData, resetPassword } from "../../../api/admin/api_admin";
import { ResetPasswordResponse } from "../../../api/admin/models_admin";
import { AdminLabel, ErrorDialog, LinkButton } from "../components";
import { validatePassword } from "../utils";

export const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id');
    
    const [data, setData] = useState<ResetPasswordResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const [password, setPassword] = useState<string>("");

    useEffect(() => {
        if (userId) {
            fetchData(userId);
        } else {
            setLoading(false);
            setError("ユーザーIDが指定されていません");
        }
    }, [userId]);

    const fetchData = async (uid: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getResetPasswordData(uid);
            if ("error" in result) {
                setError(result.error);
            } else {
                setData(result);
            }
        } catch (err) {
            setError("データの取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!userId) return;

        setSubmitting(true);
        setError(null);

        // バリデーション
        if (!password) {
            setError("パスワードは必須です");
            setSubmitting(false);
            return;
        }
        if (!validatePassword(password)) {
            setError("パスワードは半角英数字記号（大文字・小文字）のみ使用できます");
            setSubmitting(false);
            return;
        }

        try {
            const result = await resetPassword({
                userid: userId,
                password
            });

            if (result.success) {
                navigate("/menu/users");
            } else {
                setError(result.message || "パスワードの変更に失敗しました");
            }
        } catch (err) {
            setError("パスワードの変更中にエラーが発生しました");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    if (!userId || !data) {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>
                    {error ?? "ユーザーIDが指定されていません"}
                </MessageBar>
                <LinkButton link_to="/menu/users" text="戻る" />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="パスワード変更" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { maxWidth: 600 } }}>
                <Stack>
                    <Label>ユーザーID</Label>
                    <div>{data.userid}</div>
                </Stack>

                <Stack>
                    <Label>ユーザー名</Label>
                    <div>{data.username}</div>
                </Stack>

                <TextField
                    name="password"
                    label="新しいパスワード"
                    type="password"
                    value={password}
                    onChange={(_, newValue) => setPassword(newValue || "")}
                    required
                    maxLength={100}
                    description="※使用できる文字は半角英数字記号（大文字・小文字）のみです。"
                    canRevealPassword
                    autoComplete="new-password"
                />

                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="変更"
                        onClick={handleSubmit}
                        disabled={submitting || !password}
                    />
                    <LinkButton link_to="/menu/users" text="戻る" />
                </Stack>
            </Stack>
        </Stack>
    );
}